import type { AtelierActorFrame } from '../atelier-render';

/** A lateral authored swing faces the target at contact without rewriting its
 * arm keys. Sample this once at yaw=0 for each weapon's contact. */
export function weaponFacingYaw(frame:AtelierActorFrame,side:0|1,tipIndex=0):number {
  const tip=frame.anchors.weaponTips?.[tipIndex]??frame.anchors.hands[side],chest=frame.anchors.chest;
  return Math.atan2(tip[2]-chest[2],tip[0]-chest[0])+(side===1?Math.PI:0);
}

/** The contact section follows palm height: a tall fighter reaches a short
 * rival's broad head rather than penetrating toward its narrower chest. */
export function duelContactSurface(defender:AtelierActorFrame,height:number,dir:number):[number,number,number] {
  const a=defender.anchors,w=Math.max(0,Math.min(1,(height-a.neck[1])/Math.max(.04,a.head[1]-a.neck[1])));
  const radius=a.torsoRadius*(1-w)+a.headRadius*w;
  return [a.chest[0]*(1-w)+a.head[0]*w-dir*radius,height,a.chest[2]*(1-w)+a.head[2]*w];
}

/** Align the near palm with the defender's posed torso surface. The movement
 * envelope comes from the beat, while its length comes from these two recipes.
 * Mutates only the actor-owned world transform/anchors, never skin or assets. */
export function alignDuelContact(attacker:AtelierActorFrame,defender:AtelierActorFrame,side:0|1,phase:number,tipIndex=0):[number,number,number] {
  const dir=side===0?1:-1,hand=attacker.anchors.weaponTips?.[tipIndex]??attacker.anchors.hands[side];
  const target=duelContactSurface(defender,hand[1],dir);
  const rawApproach=dir*(target[0]-hand[0]);
  const approach=attacker.anchors.weaponTips?Math.max(-.22,rawApproach):Math.max(0,rawApproach);
  // A small contact plateau covers both ordinary and double-strike contact
  // times. No model-specific distance table or per-frame mesh scan is needed.
  const weight=Math.min(1,Math.max(0,phase)/.97),depth=(target[2]-hand[2])*weight;
  // A dropped frame may still be blending from the preceding pose. Never move
  // an unextended palm so far that the two bodies or skulls pass through each other.
  const aa=attacker.anchors,da=defender.anchors;
  const bodyLimit=dir*(da.chest[0]-aa.chest[0])-aa.torsoRadius-da.torsoRadius;
  const headDY=da.head[1]-aa.head[1],headDZ=da.head[2]-aa.head[2]-depth;
  const headGap=Math.sqrt(Math.max(0,(aa.headRadius+da.headRadius)**2-headDY**2-(attacker.anchors.weaponTips?0:headDZ**2)));
  const headLimit=dir*(da.head[0]-aa.head[0])-headGap;
  const shift=dir*Math.min(approach*weight,Math.max(0,Math.min(bodyLimit,headLimit)));
  attacker.model[12]+=shift;attacker.model[14]+=depth;
  for(const p of [attacker.anchors.chest,attacker.anchors.neck,attacker.anchors.head,...attacker.anchors.hands,...attacker.anchors.weaponTips??[],...attacker.anchors.weaponBases??[]]){p[0]+=shift;p[2]+=depth;}
  return [hand[0],hand[1],hand[2]];
}

